/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  JobDetailsModel, REPORT_MOBILE_APP_CHECKLIST, ReportService,
} from '@gh/builder-shared';
import {
  JobChecklistProduceDto, JobChecklistsRestService,
  reportIdField,
} from '@gh/builder-shared-data';
import {
  rendererIconByName,
  textMessageDialogRoute,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import { ID, PrintEntryPoint } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
} from '@gh/core-ui';
import {
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { GetRowIdParams } from 'ag-grid-community';
import { switchMap$ } from '@gh/rx/operators';
import { assign, noop } from 'lodash';
import { Observable } from 'rxjs';
import { JobDetailsTabBusinessService } from '../../../resources/job-details-tab';
import {
  JobChecklistItem,
} from '../../../resources/job-details-tab/job-details-tab-model';
import { openInNewWindow } from '@gh/core-util';

@Component({
  standalone: false,
             selector: 'gh-job-details-checklists-tab',
             templateUrl: 'job-details-checklists-tab.component.html',
           })
export class JobDetailsChecklistsTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  checklists: JobChecklistItem[];
  jobId: ID;

  get isViewMode(): boolean {
    return this.model && this.model.basicInfo && (this.model.basicInfo.isFinalized || this.model.basicInfo.isNotProceeding);
  }

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigationService: BuilderNavigationService,
              private businessService: JobDetailsTabBusinessService,
              private jobChecklistsRestService: JobChecklistsRestService,
              private model: JobDetailsModel,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private translationService: TranslationService,
              private userContext: UserContext,
              private report: ReportService) {
  }

  ngOnInit(): void {
    const { gridLayout, checklists, jobId } = getRouteInitData(this.route);
    assign(this, { checklists, jobId });

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      isCompleted: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
    }, {
      getRowId: (params: GetRowIdParams<JobChecklistItem>) =>
        params.data != null ? String(params.data.id) : 'row-no-data',
    });
    this.gridDefinition.bind(gridLayout);
  }

  goToAel(): void {
    this.navigationService.goToAel({
                                     entityId: this.jobId,
                                     entityType: 'JOB',
                                     label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
                                   });
  }

  onPreview(item: JobChecklistItem): void {
    this.jobChecklistsRestService.generateImage(this.jobId, item.id)
      .subscribe(() => {
        this.report
          .generateUrl(REPORT_MOBILE_APP_CHECKLIST, [reportIdField('Job', this.jobId), reportIdField('Checklist', item.id)]).pipe(
          this.waitingService.operator()).subscribe(
          (url) => {
            this.previewChecklist(url);
          },
          this.notificationService.processAll);
      },         this.notificationService.processAll);
  }

  onProduce(item: JobChecklistItem): void {
    this.businessService
      .produceChecklist(PrintEntryPoint.Produce, item).pipe(
      this.waitingService.operator(),
      switchMap$((data: JobChecklistProduceDto) => this.produceChecklist(data)))
      .subscribe({ next: noop, error: this.notificationService.processAll });
  }

  private produceChecklist(data: JobChecklistProduceDto): Observable<any> {
      const link = data.webViewLink;
      return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title: this.translationService.get('lbl_job_checklist_link_title'),
        html: `${this.translationService.get('lbl_job_checklist_link')}:\n${link}`,
        buttons: [
          { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
        ],
      }).afterClosed();
  }

  private previewChecklist(data: string): void {
    if (!this.userContext.hasPermission(REPORT_MOBILE_APP_CHECKLIST.permission)) {
      this.notificationService.error('msg_denied_no_permissions');
    } else {
      openInNewWindow(data);
    }
  }
}
